import { useState, type FormEvent } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { apiFetch, isApiError } from '../api/client'
import { AuthCard } from '../components/AuthCard'
import { safeNext, withNext } from '../context/pendingInvite'

type ForgotState = 'idle' | 'loading' | 'error' | 'sent'

export function ForgotPasswordView() {
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'))
  const [email, setEmail] = useState('')
  const [state, setState] = useState<ForgotState>('idle')
  const [errorMsg, setErrorMsg] = useState('')

  async function handleSubmit(e: FormEvent) {
    e.preventDefault()
    setState('loading')
    setErrorMsg('')
    try {
      // Always 200 {sent: true} for a well-formed address: the answer never says
      // whether an account exists, and neither does this screen.
      await apiFetch('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: email.trim() }),
      })
      setState('sent')
    } catch (err) {
      setState('error')
      const s = isApiError(err) ? err.status : 0
      if (s === 422) setErrorMsg('Enter a valid email address.')
      else if (s === 429) {
        const mins = Math.max(1, Math.ceil(((isApiError(err) && err.retry_after_seconds) || 900) / 60))
        setErrorMsg(`Too many reset requests from this network. Try again in about ${mins} minute${mins === 1 ? '' : 's'}.`)
      } else setErrorMsg('Password reset is temporarily unavailable. Please try again in a moment.')
    }
  }

  const back = <Link to={withNext('/login', next)}>Back to sign in</Link>

  if (state === 'sent') {
    return (
      <AuthCard title="Check your email" footer={back}>
        <p role="status" className="form-notice">
          If an account exists for that address, we sent a link to reset your password. The
          link can be used once.
        </p>
      </AuthCard>
    )
  }

  const busy = state === 'loading'
  return (
    <AuthCard
      title="Reset your password"
      subtitle="Enter your account email and we’ll send you a reset link."
      footer={back}
    >
      <form onSubmit={handleSubmit} aria-label="Forgot password form" noValidate>
        <div className="field">
          <label htmlFor="forgot-email">Email</label>
          <input id="forgot-email" type="email" autoComplete="email" value={email}
            onChange={(e) => setEmail(e.target.value)} disabled={busy} required />
        </div>
        {state === 'error' && <p role="alert" className="form-error">{errorMsg}</p>}
        <button type="submit" className="btn btn--primary btn--block"
          disabled={busy || !email.trim()} aria-busy={busy}>
          {busy ? 'Sending…' : 'Send reset link'}
        </button>
      </form>
    </AuthCard>
  )
}
